import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["sidebar", "label", "logoText", "collapseButton", "expandButton", "navItem", "footerNavItem", "userInfo", "logoLink", "header", "userContainer", "navContainer", "footerContainer", "userWrapper", "navListExpanded", "navListCollapsed", "footerListExpanded", "footerListCollapsed", "logoutExpanded", "logoutCollapsed", "teamSwitcherExpanded", "teamSwitcherCollapsed", "teamName"]

  connect() {
    // Check localStorage for saved state
    const isCollapsed = localStorage.getItem("sidebarCollapsed") === "true"
    if (isCollapsed) {
      this.collapseInstant() // Instant collapse on load
    } else {
      this.ensureTeamNameReadable()
    }
  }

  toggle() {
    if (this.sidebarTarget.classList.contains("w-[68px]")) {
      this.expand()
    } else {
      this.collapse()
    }
  }

  showExpandButton() {
    // Only show expand button when sidebar is collapsed
    if (this.sidebarTarget.classList.contains("w-[68px]")) {
      // Hide logo link
      if (this.hasLogoLinkTarget) {
        this.logoLinkTarget.classList.add("opacity-0")
      }

      // Show expand button in logo's position
      if (this.hasExpandButtonTarget) {
        this.expandButtonTarget.classList.remove("opacity-0")
        this.expandButtonTarget.classList.add("opacity-100")
      }
    }
  }

  hideExpandButton() {
    // Show logo link back
    if (this.hasLogoLinkTarget) {
      this.logoLinkTarget.classList.remove("opacity-0")
    }

    // Hide expand button
    if (this.hasExpandButtonTarget) {
      this.expandButtonTarget.classList.remove("opacity-100")
      this.expandButtonTarget.classList.add("opacity-0")
    }
  }

  collapse() {
    // Change sidebar width
    this.sidebarTarget.classList.remove("w-[240px]")
    this.sidebarTarget.classList.add("w-[68px]")

    // Hide all text labels
    this.labelTargets.forEach(label => {
      label.classList.add("hidden")
    })

    // Hide logo text
    if (this.hasLogoTextTarget) {
      this.logoTextTarget.classList.add("hidden")
    }

    // Hide user info text
    if (this.hasUserInfoTarget) {
      this.userInfoTarget.classList.add("hidden")
    }

    // Hide collapse button (left arrow), show expand button (right arrow)
    if (this.hasCollapseButtonTarget) {
      this.collapseButtonTarget.classList.add("hidden")
    }
    if (this.hasExpandButtonTarget) {
      this.expandButtonTarget.classList.remove("hidden")
      this.expandButtonTarget.classList.add("flex")
    }

    // Center header content
    if (this.hasHeaderTarget) {
      this.headerTarget.classList.add("justify-center")
      this.headerTarget.classList.remove("justify-between")
    }

    // Center logo link and remove left padding
    if (this.hasLogoLinkTarget) {
      this.logoLinkTarget.classList.remove("pl-6")
    }

    // Remove padding from nav containers when collapsed
    if (this.hasNavContainerTarget) {
      this.navContainerTarget.classList.remove("px-4")
      this.navContainerTarget.classList.add("px-0")
    }
    if (this.hasFooterContainerTarget) {
      this.footerContainerTarget.classList.remove("px-4")
      this.footerContainerTarget.classList.add("px-0")
    }

    // Update main nav item styling - center icons with fixed width (52px)
    this.navItemTargets.forEach(item => {
      // Skip footer items as they're handled separately
      if (!item.hasAttribute("data-sidebar-target") || !item.getAttribute("data-sidebar-target").includes("footerNavItem")) {
        item.classList.add("justify-center")
        item.classList.remove("gap-3")
        item.classList.add("w-[52px]")
        item.classList.add("mx-auto")
        item.classList.remove("flex-1")
      }
    })

    // Update footer nav item styling - same as main nav items
    if (this.hasFooterNavItemTarget) {
      this.footerNavItemTargets.forEach(item => {
        item.classList.add("justify-center")
        item.classList.remove("gap-3")
        item.classList.remove("px-3")
        item.classList.add("px-0")
        item.classList.add("w-[52px]")
        item.classList.add("mx-auto")
        item.classList.remove("flex-1")
      })
    }

    // Center user container and remove padding
    if (this.hasUserContainerTarget) {
      this.userContainerTarget.classList.add("justify-center")
      this.userContainerTarget.classList.remove("px-2")
      this.userContainerTarget.classList.add("px-0")
    }

    // Remove flex-1 from user wrapper to prevent stretching
    if (this.hasUserWrapperTarget) {
      this.userWrapperTarget.classList.remove("flex-1")
    }

    // Switch to collapsed nav lists (with tooltips)
    this.showCollapsedLists()

    // Toggle team switcher
    if (this.hasTeamSwitcherExpandedTarget) {
      this.teamSwitcherExpandedTarget.classList.add("hidden")
    }
    if (this.hasTeamSwitcherCollapsedTarget) {
      this.teamSwitcherCollapsedTarget.classList.remove("hidden")
      this.teamSwitcherCollapsedTarget.classList.add("flex")
    }

    // Toggle logout buttons
    if (this.hasLogoutExpandedTarget) {
      this.logoutExpandedTarget.classList.add("hidden")
    }
    if (this.hasLogoutCollapsedTarget) {
      this.logoutCollapsedTarget.classList.remove("hidden")
      this.logoutCollapsedTarget.classList.add("flex")
    }

    // Save state
    localStorage.setItem("sidebarCollapsed", "true")

  }

  collapseInstant() {
    // Same as collapse but without transitions
    this.collapse()
  }

  expand() {
    // Change sidebar width
    this.sidebarTarget.classList.remove("w-[68px]")
    this.sidebarTarget.classList.add("w-[240px]")

    // Show all text labels
    this.labelTargets.forEach(label => {
      label.classList.remove("hidden")
    })

    // Show logo text
    if (this.hasLogoTextTarget) {
      this.logoTextTarget.classList.remove("hidden")
    }

    // Show user info text
    if (this.hasUserInfoTarget) {
      this.userInfoTarget.classList.remove("hidden")
    }

    // Show collapse button (left arrow), hide expand button (right arrow)
    if (this.hasCollapseButtonTarget) {
      this.collapseButtonTarget.classList.remove("hidden")
    }
    if (this.hasExpandButtonTarget) {
      this.expandButtonTarget.classList.add("hidden")
      this.expandButtonTarget.classList.remove("flex")
      this.expandButtonTarget.classList.add("opacity-0")
      this.expandButtonTarget.classList.remove("opacity-100")
    }

    // Restore header layout
    if (this.hasHeaderTarget) {
      this.headerTarget.classList.remove("justify-center")
      this.headerTarget.classList.add("justify-between")
    }

    // Restore logo link visibility and left padding
    if (this.hasLogoLinkTarget) {
      this.logoLinkTarget.classList.remove("opacity-0")
      this.logoLinkTarget.classList.add("pl-6")
    }

    // Restore padding to nav containers when expanded
    if (this.hasNavContainerTarget) {
      this.navContainerTarget.classList.remove("px-0")
      this.navContainerTarget.classList.add("px-4")
    }
    if (this.hasFooterContainerTarget) {
      this.footerContainerTarget.classList.remove("px-0")
      this.footerContainerTarget.classList.add("px-4")
    }

    // Update main nav item styling - restore gap and make full width
    this.navItemTargets.forEach(item => {
      // Skip footer items as they're handled separately
      if (!item.hasAttribute("data-sidebar-target") || !item.getAttribute("data-sidebar-target").includes("footerNavItem")) {
        item.classList.remove("justify-center")
        item.classList.add("gap-3")
        item.classList.remove("w-[52px]")
        item.classList.remove("mx-auto")
        item.classList.add("flex-1")
      }
    })

    // Restore footer nav item styling with full width
    if (this.hasFooterNavItemTarget) {
      this.footerNavItemTargets.forEach(item => {
        item.classList.remove("justify-center")
        item.classList.add("gap-3")
        item.classList.add("px-3")
        item.classList.remove("px-0")
        item.classList.remove("w-[52px]")
        item.classList.remove("mx-auto")
        item.classList.add("flex-1")
      })
    }

    // Restore user container alignment and padding
    if (this.hasUserContainerTarget) {
      this.userContainerTarget.classList.remove("justify-center")
      this.userContainerTarget.classList.remove("px-0")
      this.userContainerTarget.classList.add("px-2")
    }

    // Restore flex-1 to user wrapper
    if (this.hasUserWrapperTarget) {
      this.userWrapperTarget.classList.add("flex-1")
    }

    // Switch to expanded nav lists (no tooltips)
    this.showExpandedLists()

    // Toggle team switcher
    if (this.hasTeamSwitcherExpandedTarget) {
      this.teamSwitcherExpandedTarget.classList.remove("hidden")
    }
    if (this.hasTeamSwitcherCollapsedTarget) {
      this.teamSwitcherCollapsedTarget.classList.remove("flex")
      this.teamSwitcherCollapsedTarget.classList.add("hidden")
    }
    this.ensureTeamNameReadable()

    // Toggle logout buttons
    if (this.hasLogoutExpandedTarget) {
      this.logoutExpandedTarget.classList.remove("hidden")
    }
    if (this.hasLogoutCollapsedTarget) {
      this.logoutCollapsedTarget.classList.remove("flex")
      this.logoutCollapsedTarget.classList.add("hidden")
    }

    // Save state
    localStorage.setItem("sidebarCollapsed", "false")

  }

  // Show collapsed nav lists (with tooltips)
  showCollapsedLists() {
    // Hide expanded lists
    if (this.hasNavListExpandedTarget) {
      this.navListExpandedTarget.classList.remove("flex")
      this.navListExpandedTarget.classList.add("hidden")
    }
    if (this.hasFooterListExpandedTarget) {
      this.footerListExpandedTarget.classList.remove("flex")
      this.footerListExpandedTarget.classList.add("hidden")
    }

    // Show collapsed lists
    if (this.hasNavListCollapsedTarget) {
      this.navListCollapsedTarget.classList.remove("hidden")
      this.navListCollapsedTarget.classList.add("flex")
    }
    if (this.hasFooterListCollapsedTarget) {
      this.footerListCollapsedTarget.classList.remove("hidden")
      this.footerListCollapsedTarget.classList.add("flex")
    }
  }

  // Show expanded nav lists (no tooltips)
  showExpandedLists() {
    // Show expanded lists
    if (this.hasNavListExpandedTarget) {
      this.navListExpandedTarget.classList.remove("hidden")
      this.navListExpandedTarget.classList.add("flex")
    }
    if (this.hasFooterListExpandedTarget) {
      this.footerListExpandedTarget.classList.remove("hidden")
      this.footerListExpandedTarget.classList.add("flex")
    }

    // Hide collapsed lists
    if (this.hasNavListCollapsedTarget) {
      this.navListCollapsedTarget.classList.remove("flex")
      this.navListCollapsedTarget.classList.add("hidden")
    }
    if (this.hasFooterListCollapsedTarget) {
      this.footerListCollapsedTarget.classList.remove("flex")
      this.footerListCollapsedTarget.classList.add("hidden")
    }
  }

  ensureTeamNameReadable() {
    if (!this.hasTeamNameTarget) return

    this.teamNameTarget.classList.remove("text-white")
    this.teamNameTarget.classList.add("text-gray-900")
  }
}
